/**
 * Roster publish gate applicability registry.
 *
 * WHY THIS EXISTS
 * ---------------
 * Every roster publish gate used to answer three questions at one raise site:
 * is this true, does it matter for this provider, and how much. Only the third
 * was ever configurable, and only for one rule. The result, measured on
 * production 2026-08-02:
 *
 *   Golden Bloom (home care)  335 PAY_CONTEXT_UNRESOLVED BLOCK with
 *                                 pay_context_enforcement_mode = OFF
 *                              14 CARE_MINUTES_SHORTFALL — an AN-ACC
 *                                 residential rule, on a home-care provider
 *   Medea Park (residential)  109 PAY_ROLE_MAPPING_REQUIRED BLOCK with
 *                                 pay_context_enforcement_mode = SHADOW
 *
 * This registry separates the three questions:
 *
 *   DETECTION      is it true, and how bad is THIS instance   (detection fns)
 *   APPLICABILITY  does this rule exist for this service model (here)
 *   ENFORCEMENT    given both — block, warn, or just record    (here + tenant mode)
 *
 * DESIGN CONSTRAINTS, from adversarial review (Cursor + Grok, 2026-08-02)
 * ----------------------------------------------------------------------
 * 1. NEVER SUPPRESS. A finding that is inapplicable or unenforced is still
 *    recorded, at INFO. Dropping it destroys the override-reason feedback loop
 *    that is meant to reveal defective gates, and risks silencing a real
 *    breach when a mode is misconfigured.
 * 2. POLICY CAPS, IT DOES NOT ASSIGN. Detection owns instance severity
 *    (e.g. gates-runner.ts:1493 varies WARN/INFO on threshold). The registry
 *    supplies a CEILING. Effective = min(detected, ceiling). A relaxed mode can
 *    only ever lower severity, so a misconfigured mode cannot manufacture a
 *    blocker.
 * 3. SAFETY RULES CANNOT BE SOFTENED. `neverSoften` rules ignore tenant mode
 *    entirely and always sit at their enforced ceiling.
 * 4. COMPLETENESS BY COMPILER. `RosterGateRuleCode` is derived from this
 *    object. Raise sites import from it. Source-scanning for rule codes was
 *    tried and failed four times — literals, constants, ternaries and
 *    cross-file raises each defeated it, and two of the eight non-overridable
 *    safety codes were silently missed.
 */

export type GateServiceModel = 'HOME_CARE' | 'RESIDENTIAL';

// Reuse the detection-side severity type rather than declaring a parallel one —
// a second definition could drift and silently break the severity cap.
export type { GateSeverity } from './roster-gate-detection';
import type { GateSeverity } from './roster-gate-detection';

/** tenant_configs columns that graduate enforcement. */
export type GateGoverningMode =
  | 'worker_compliance_enforcement_mode'
  | 'pay_context_enforcement_mode'
  | 'roster_coverage_enforcement_mode';

export type RosterPublicationKind =
  | 'PAY_ONLY'
  | 'CLASSIFICATION_SCOPE'
  | 'CLINICAL_OR_LEGAL'
  | 'SCOPE_INTEGRITY'
  | 'EXISTING';
export type GateApplicability = {
  /** D21 publication purpose. This exhaustive registry also governs pay holds. */
  readonly publicationKind: RosterPublicationKind;
  /** Service models where this rule is meaningful at all. */
  readonly serviceModels: readonly GateServiceModel[];
  /** Tenant mode graduating enforcement. Absent = always at enforcedSeverity. */
  readonly governingMode?: GateGoverningMode;
  /** Ceiling when the governing mode is enforcing (or when there is none). */
  readonly enforcedSeverity: GateSeverity;
  /** Ceiling when the mode is not enforcing. Never below INFO — see constraint 1. */
  readonly relaxedSeverity: GateSeverity;
  /**
   * Safety rules that no tenant configuration may soften. Mirrors the
   * non-overridable set enforced by roster_publish_rule_is_non_overridable()
   * and derived in TS by isNonOverridableRosterPublishRule().
   */
  readonly neverSoften?: true;
  /**
   * Unit the rule reasons about. Where this disagrees with what detection
   * actually measures, the rule has a known defect — see KNOWN_UNIT_DEFECTS.
   */
  readonly unitOfWork?: 'SHIFT' | 'PERIOD_OF_WORK';
  /** Why the rule exists and who it is for. Required; asserted non-trivial. */
  readonly rationale: string;
};

const REGISTRY = {
  // ── Coverage ──────────────────────────────────────────────────────────
  WORK_RESTRICTION_ACTIVE: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    unitOfWork: 'SHIFT',
    rationale:
      'Provider-held worker work restrictions require a visible roster warning and a recorded reason to acknowledge; they never prevent needed care.',
  },
  SCREENING_REQUIREMENT_MISSING: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    unitOfWork: 'SHIFT',
    rationale:
      'A configured provider screening requirement with no current verified evidence needs review and a reasoned acknowledgement; it never prevents needed care.',
  },
  UNFILLED_ROSTER_SLOT: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'roster_coverage_enforcement_mode',
    enforcedSeverity: 'BLOCK',
    // Coverage WARN (home-care pilot default): mid-period unfilled visits are
    // normal while the fortnight is still being filled — surface WARN, do not
    // dead-end publish. Residential tenants that stay on ENFORCE keep BLOCK.
    // Policy also caps HOME_CARE at WARN even under ENFORCE (see decideGate).
    // DB trigger softens independently via roster_coverage_enforcement_mode.
    relaxedSeverity: 'WARN',
    rationale:
      'Residential: an unfilled shift means residents unattended. Home care: a normal mid-fortnight state while visits are filled.',
  },
  UNFILLED_AGENCY_ROSTER_SLOT: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'WARN',
    // Supplier coverage is advisory regardless of the provider's internal
    // roster-coverage mode: it must remain visible without becoming a publish
    // blocker or disappearing when the mode is relaxed.
    relaxedSeverity: 'WARN',
    unitOfWork: 'SHIFT',
    rationale:
      'An agency-designated slot still needs supplier acceptance evidence; surface the unfilled assumption without treating it as an internal roster blocker.',
  },

  // ── Residential-only ──────────────────────────────────────────────────
  CARE_MINUTES_SHORTFALL: {
    publicationKind: 'EXISTING',
    serviceModels: ['RESIDENTIAL'],
    governingMode: 'roster_coverage_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale:
      'AN-ACC mandatory care minutes are a residential funding obligation. Home-care providers have no care-minutes target.',
  },
  RN_24_7_SCHEDULED_GAP: {
    publicationKind: 'EXISTING',
    serviceModels: ['RESIDENTIAL'],
    governingMode: 'roster_coverage_enforcement_mode',
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'WARN',
    rationale:
      '24/7 registered nurse coverage is a residential facility obligation and has no home-care equivalent.',
  },

  // ── Home-care-only ────────────────────────────────────────────────────
  SAH_CARE_PLAN_NOT_DELIVERY_READY: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE'],
    // Publish is advisory: assign may already create SCHEDULED services without
    // financial-ready (20270897000000). Start/complete remain care-first;
    // financial posting and claims stay hard in their owning RPCs.
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    rationale: 'Support at Home financial readiness governs posting and claiming, not care.',
  },
  NDIS_CARE_PLAN_NOT_ROSTER_READY: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE'],
    // Detection chooses BLOCK (hard: inactive plan / no inclusions / client mismatch)
    // or WARN (soft billing identity). Ceiling remains BLOCK so hard cases stay hard.
    governingMode: 'roster_coverage_enforcement_mode',
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'WARN',
    rationale:
      'NDIS plan operational readiness. Hard-block only when care cannot be scheduled (inactive plan, no NDIA inclusions, client mismatch). Billing identity gaps warn so providers can still roster care; stamp/invoice stay fail-closed.',
  },
  SAH_VISIT_NOT_FUNDED: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE'],
    // Publish surfaces the funding gap; claim/financials stay fail-closed.
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    rationale:
      'A Support at Home visit with no funded service inclusion may not be claimable. Residential shifts are funded per resident-day, not per visit.',
  },
  SAH_IMPOSSIBLE_TRAVEL: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE'],
    /*
       GRADUATED, and this is load-bearing.

       Without a governingMode the GateApplicability contract puts a rule at
       enforcedSeverity for EVERY tenant ("Absent = always at enforcedSeverity"),
       so this sat at BLOCK for every home-care provider the moment it could
       fire. It cannot fire today only because `expected_travel_minutes` is null
       on all 515 Golden Bloom visits — an accident, not a control.

       That matters because the only available estimator is explicitly
       uncalibrated. `roster-travel-estimate.ts` states in its own header: "Do
       not arm SAH_IMPOSSIBLE_TRAVEL (BLOCK) from this without calibration; a
       false IMPOSSIBLE stops a legitimate publish and destroys coordinator
       trust." Its two error sources run in OPPOSITE directions with unknown net
       — straight-line distance understates road distance, while the assumed
       40 km/h overstates minutes — so it can manufacture an IMPOSSIBLE verdict
       on a comfortably achievable hop. Coverage is partial too: 14 of Golden
       Bloom's 24 rostered clients carry coordinates.

       Populating travel minutes is worth doing so coordinators can SEE travel.
       Doing so while this rule sits at an ungraduated BLOCK would hard-block
       publishes on an unvalidated heuristic. Graduating it means travel becomes
       visible at WARN, and a provider opts into blocking only once the estimate
       has been calibrated against their real rosters.
    */
    governingMode: 'roster_coverage_enforcement_mode',
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'WARN',
    rationale: 'Travel feasibility between client homes. Residential shifts are on one site.',
  },
  SAH_TIGHT_TRAVEL: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE'],
    // Same governing mode as its BLOCK sibling so the pair graduate together.
    // A tight-travel WARN alongside an impossible-travel WARN is coherent; the
    // two moving independently would let a provider see the softer signal
    // escalate while the harder one did not.
    governingMode: 'roster_coverage_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale: 'Travel margin between client homes. No residential equivalent.',
  },
  FUNDING_AUTHORISATION_MISSING: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE'],
    /*
       RCA-2.5. Split out of FUNDING_AUTHORISATION_EXCEEDED, which was reporting
       it as an overspend.

       `funding_authorisations` is EMPTY on production (0 rows, 2026-08-25), so
       `assert_funding_authorisation_remaining` raises
       `funding_authorisation_not_found` (ERRCODE P0002) for every SAH visit.
       The gate runner caught any RPC error and raised EXCEEDED, and
       friendlyRosterError renders P0002 as the generic "The roster record could
       not be found". The result, on 99 live gates: coordinators were told
       "Funding authorisation cap check failed ... The roster record could not be
       found", with the remedy "reduce the rostered service cost, or reallocate
       funding".

       Every part of that was wrong. Nothing was exceeded, no roster record was
       missing, and neither suggested action can fix an absent authorisation.

       Graduated rather than flat BLOCK, following SAH_VISIT_NOT_FUNDED: a
       provider with no authorisations loaded would otherwise be unable to
       publish at all, which interrupts care to fix a finance record.

       PINNED TO WARN, owner decision 2026-08-25. An external audit (grok) found
       that this split converted 99 gates that COULD NEVER BLOCK into gates that
       block the moment a tenant's pay_context_enforcement_mode moves off
       OFF/SHADOW. Golden Bloom holds exactly those 99 OPEN today, and
       funding_authorisations is still empty, so enabling enforcement for any
       other reason would have stopped their publishing with no warning and no
       action that fixes it.

       The rule it replaced, FUNDING_AUTHORISATION_EXCEEDED, carries
       enforcedSeverity WARN and cannot block. Pinning this one to WARN restores
       that property, so the split is a CORRECTNESS fix to the message and the
       remedy, not a quiet escalation of consequence.

       THE COST, stated because it is real: this rule can now never block, in
       any enforcement mode. A provider delivering Support at Home against no
       funding authorisation will be warned and will still publish. Restoring
       enforcement means changing enforcedSeverity back to BLOCK deliberately,
       once authorisations are actually loaded — which is the condition that
       makes the block meaningful rather than universal.
    */
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    rationale:
      'No Support at Home funding authorisation covers this client, program and pool for the service date. Residential funding is AN-ACC, not per-participant authorisation.',
  },
  FUNDING_AUTHORISATION_UNVERIFIABLE: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE'],
    /*
       RCA-2.5. The remaining error branch: the cap check itself failed for a
       reason that is neither "no authorisation" nor "over cap" — a timeout, a
       permission refusal, an ambiguous authorisation.

       It stays visible at WARN rather than being swallowed, because a check
       that did not run is not a check that passed. It is deliberately NOT
       BLOCK: an infrastructure fault is not evidence the provider did anything
       wrong, and blocking a publish on one punishes the wrong party.
    */
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    rationale:
      'The Support at Home funding cap could not be verified. Unverified is recorded, never treated as verified.',
  },
  FUNDING_AUTHORISATION_EXCEEDED: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE'],
    // Publish advisory so coordinators can schedule while budgets catch up.
    // apply_sah_service_financials / claim paths remain hard for overspend.
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    rationale:
      'SAH participant budget authorisation. Residential funding is AN-ACC, not per-participant budget.',
  },
  FUNDING_AUTHORISATION_THRESHOLD: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale:
      'Support at Home participant budget is approaching its authorised threshold for the period.',
  },
  FUNDING_AUTHORISATION_ALLOCATION_REQUIRED: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE'],
    // Missing allocation is setup lag at publish; money finality stays hard.
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    rationale: 'SAH funding allocation must exist before financial posting, not before care.',
  },

  // ── Pay context — governed by pay_context_enforcement_mode ────────────
  PAY_CONTEXT_UNRESOLVED: {
    publicationKind: 'PAY_ONLY',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'INFO',
    rationale:
      'Pay context must resolve before payroll export; irrelevant at publish when the tenant is not enforcing pay context.',
  },
  PAY_ROLE_MAPPING_REQUIRED: {
    publicationKind: 'PAY_ONLY',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'INFO',
    rationale: 'Multi-role payroll cannot infer pay context from a staff default.',
  },
  PAY_ROLE_MAPPING_INCOMPLETE: {
    publicationKind: 'PAY_ONLY',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale:
      'Pay role is mapped but the mapping is incomplete for this shift date, so cost cannot be fully derived.',
  },
  PAY_ROLE_NOT_ACTIVE: {
    publicationKind: 'PAY_ONLY',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'INFO',
    rationale:
      'The pay role assigned to this worker is no longer active, so the shift cannot be costed against it.',
  },
  PAY_ROLE_ELIGIBILITY_MISSING: {
    publicationKind: 'PAY_ONLY',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale:
      'Evidence that the worker is eligible for the assigned pay role has not been recorded.',
  },
  PAY_ROLE_REQUIREMENT_KIND_UNKNOWN: {
    publicationKind: 'SCOPE_INTEGRITY',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale:
      'The pay-role requirement kind is unrecognised, so eligibility cannot be evaluated automatically.',
  },
  // KNOWN_BAD per classifyComplianceRule. Not mode-governed: publishing a
  // shift that provably cannot be costed should require an attestation rather
  // than pass silently. Zero live instances on either tenant, so this costs
  // nothing today and keeps the KNOWN_BAD rule uniform.
  RATE_VERSION_MISSING: {
    publicationKind: 'PAY_ONLY',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'BLOCK',
    rationale:
      'No effective-dated rate version covers the shift date for the worker award, so the shift provably cannot be costed.',
  },
  NON_HOURLY_PAY_PROFILE_MANUAL_REVIEW: {
    publicationKind: 'PAY_ONLY',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale: 'Salary/non-hourly profiles need manual cost review.',
  },
  WAGE_FORECAST_MANUAL_REVIEW: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale: 'Wage forecast could not be derived automatically.',
  },

  // ── Worker compliance ─────────────────────────────────────────────────
  MISSING_SCREENING: {
    publicationKind: 'CLINICAL_OR_LEGAL',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    /*
       MODE-GOVERNED, and the reasoning matters because this shipped wrong once.

       An earlier revision made this `neverSoften: true` on the argument that
       WARN may soften a provider's own training rules but must not soften the
       Act's screening obligation. That argument is sound about the OBLIGATION
       and wrong about the CONTROL, because it ignores what absence means.

       This rule fires on the ABSENCE of a screening record, and absence is the
       default state for a provider who has not yet loaded their compliance
       data. Measured on production when the block was written: Medea Park had
       87 of 87 active workers with no screening row, Golden Bloom 14 of 16.
       Enforcing at BLOCK would not have improved compliance — it would have
       refused every direct-care assignment and every publish at both live
       providers, stopping care delivery outright.

       `worker_compliance_enforcement_mode` exists precisely for this window: a
       provider operating while they onboard evidence. Under WARN the gap is
       still detected and recorded at INFO, and is summarised through the
       adoption/backfill tracker instead of repeated as a warning on every
       shift. Once a provider loads their data and moves to ENFORCE_ROSTER or
       ENFORCE_ALL, this becomes a BLOCK with the attestation runway (stat dec +
       supervision + lodged application + ≤90-day follow-up) as the regulated
       exit.
    */
    governingMode: 'worker_compliance_enforcement_mode',
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'INFO',
    rationale:
      'Screening not yet evidenced for direct-care or a configured requirement. Recorded as informational backfill evidence under WARN; blocks with an attestation exit once compliance enforcement is on.',
  },
  MISSING_REQUIRED_TRAINING: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    rationale:
      'Provider policy evidence gap. Warn in every enforcement mode; tenant opt-in and an audited manager override are checked at first assignment.',
  },
  REQUIRED_ROLE_CERTIFICATION_MISSING: {
    publicationKind: 'CLINICAL_OR_LEGAL',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'worker_compliance_enforcement_mode',
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'WARN',
    rationale: 'Role certification not evidenced. Attestation-eligible.',
  },
  // KNOWN_BAD per classifyComplianceRule: the worker is known to be below
  // requirement, not merely missing evidence. Deliberately NOT mode-governed.
  // These stay an overridable BLOCK so a human must attest before publish;
  // softening to WARN would let it publish silently. INSUFFICIENT_CLASSIFICATION
  // in particular is raised for roleRequirementKind CLINICAL_SCOPE_MINIMUM
  // ("reassign to an appropriately-scoped worker"), so it can encode scope of
  // practice, not just an award pay band.
  MISSING_CLASSIFICATION: {
    publicationKind: 'PAY_ONLY',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    /*
       Same absence problem as MISSING_SCREENING: this fires when a worker has
       no award classification evidence covering the shift date, and that is the
       default for a provider who has not loaded classification data. Medea had
       30 of these blocking publish while holding zero screening rows and no
       configured staffing requirements — an onboarding gap, not a rostering
       decision. Recorded and visible under WARN; blocks once enforcement is on.
    */
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'BLOCK',
    rationale:
      'Worker has no award classification or scope evidence covering the shift date. Known-bad state, so publish requires an explicit attestation rather than a silent warning.',
  },
  INSUFFICIENT_CLASSIFICATION: {
    publicationKind: 'CLASSIFICATION_SCOPE',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'BLOCK',
    rationale:
      'Worker classification or clinical scope is below what the shift requires. Raised for CLINICAL_SCOPE_MINIMUM as well as industrial minimums, so it may encode scope of practice.',
  },
  ASSIGNMENT_REQUIREMENT_WARNING: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'worker_compliance_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale:
      'A non-blocking assignment requirement is unmet; the shift may proceed with the gap recorded.',
  },

  // Worker screening is governed as one control family: WARN means the
  // provider has explicitly accepted temporary screening risk while evidence
  // is backfilled. Known expired screening therefore follows the same mode as
  // missing screening, but returns to a non-overridable BLOCK when enforcement
  // is enabled. This exception does not extend to AHPRA or work rights.
  EXPIRED_SCREENING: {
    publicationKind: 'CLINICAL_OR_LEGAL',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'worker_compliance_enforcement_mode',
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'INFO',
    rationale:
      'Known expired or invalid worker screening. Recorded as informational backfill evidence under WARN; non-overridable and blocking once roster compliance enforcement is enabled.',
  },
  // ── Safety: never softened by any tenant mode ─────────────────────────
  EXPIRED_REQUIRED_TRAINING: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    rationale:
      'Provider policy evidence gap. Warn in every enforcement mode; tenant opt-in and an audited manager override are checked at first assignment.',
  },
  AHPRA_REGISTRATION_REVIEW_REQUIRED: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    rationale:
      'Default-OFF provider assessment of actual clinical function using canonical independently verified registration; activation does not create a new care refusal.',
  },
  AHPRA_REGISTRATION_MISSING: {
    publicationKind: 'CLINICAL_OR_LEGAL',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'BLOCK',
    neverSoften: true,
    rationale: 'Regulated health practitioner without registration evidence.',
  },
  AHPRA_REGISTRATION_EXPIRED: {
    publicationKind: 'CLINICAL_OR_LEGAL',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'BLOCK',
    neverSoften: true,
    rationale:
      'AHPRA registration has lapsed. A regulated practitioner cannot practise unregistered, in any configuration.',
  },
  WORK_RIGHTS_EXPIRED: {
    publicationKind: 'CLINICAL_OR_LEGAL',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'BLOCK',
    neverSoften: true,
    rationale: 'Expired work rights. Rostering is unlawful regardless of provider configuration.',
  },
  ROLE_CERTIFICATION_INVALID_OR_EXPIRED: {
    publicationKind: 'CLINICAL_OR_LEGAL',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'BLOCK',
    neverSoften: true,
    rationale: 'Role certification is invalid or expired. Known-bad state, not an evidence gap.',
  },
  ASSIGNMENT_REQUIREMENT_BLOCKED: {
    publicationKind: 'CLINICAL_OR_LEGAL',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'BLOCK',
    neverSoften: true,
    rationale: 'Assignment requirement hard-blocked by statutory guard.',
  },
  ENFORCED_POLICY_ACKNOWLEDGEMENT_REQUIRED: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    rationale:
      'Provider policy evidence gap. Warn in every enforcement mode; tenant opt-in and an audited manager override are checked at first assignment.',
  },

  // ── Fatigue and hours ─────────────────────────────────────────────────
  TEN_HOUR_BREAK: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'roster_coverage_enforcement_mode',
    // WARN in every mode from 2026-09-01 on Paul's direction, for the same
    // reason BROKEN_SHIFT_SPAN_EXCEEDED warns: the award does not prohibit the
    // short rest, it prices it. Under cl 25.4(c) an employee who starts without
    // 10 consecutive hours off "must be released until they have had 10
    // consecutive hours off duty without loss of pay" — an entitlement that
    // triggers, not a bar on rostering. A provider may knowingly accept that
    // cost, so the roster surfaces it rather than refusing to publish.
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    /*
       Detection measures PERIOD_OF_WORK: same-day home-care visits are periods
       of one broken shift (cl 25.6(a)(i)) and are exempt; rest is measured
       between successive days per cl 25.6(g).

       Previously BLOCK under an enforcing mode and WARN under a relaxed one.
       Now WARN in both — see the note on enforcedSeverity above.

       Worth stating plainly, because this one differs from the screening and
       classification gates softened alongside it: TEN_HOUR_BREAK needs NO
       loaded compliance data to be correct — it is computed from the roster
       itself. Breaches are therefore likely to be genuine cross-day short-rest
       events with an award basis, not onboarding lag. Warning is a decision to
       let the provider carry the cl 25.4(c) release-and-pay consequence
       knowingly; it is NOT a statement that the breach is not real. They still
       warrant review rather than being left to accumulate.

       Enterprise agreements can be stricter than the award. Where an EA bars
       the short rest outright rather than pricing it, WARN is not sufficient
       and that tenant needs a stricter setting.
    */
    unitOfWork: 'PERIOD_OF_WORK',
    rationale:
      'SCHADS cl 25.4(a) generally, cl 25.6(g) for home care — 10h between the end of one shift and the start of the next.',
  },
  BROKEN_SHIFT_SPAN_EXCEEDED: {
    publicationKind: 'EXISTING',
    // cl 25.6 reaches home care and SACS disability-services employees only.
    serviceModels: ['HOME_CARE'],
    // WARN, not BLOCK, because cl 25.6(f) does not prohibit the span — it prices
    // it: "All work performed beyond a span of 12 hours will be paid at double
    // time." That is a cost the provider may knowingly accept, so it warns.
    //
    // It also closes a hole opened by the cl 25.6(g) unit fix: a same-day pair
    // like 06:00-14:00 and 20:00-23:59 used to raise TEN_HOUR_BREAK on the
    // 6-hour gap, mislabelled but visible. Exempting same-day pairs removes that
    // signal, and an 18-hour span is the thing actually worth surfacing.
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    unitOfWork: 'PERIOD_OF_WORK',
    rationale:
      'MA000100 cl 25.6(f) — the span of a broken shift is up to 12 hours; work beyond it is paid at double time.',
  },
  EXCESSIVE_DAILY_HOURS: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'WARN',
    // Fatigue thresholds are facts about hours worked, not a posture a provider graduates into. Never relaxes; relaxedSeverity equals enforcedSeverity deliberately.
    relaxedSeverity: 'WARN',
    unitOfWork: 'PERIOD_OF_WORK',
    rationale: 'SCHADS daily ordinary-hours ceiling before overtime provisions engage.',
  },
  EXCESSIVE_WEEKLY_HOURS: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'WARN',
    // Fatigue thresholds are facts about hours worked, not a posture a provider graduates into. Never relaxes; relaxedSeverity equals enforcedSeverity deliberately.
    relaxedSeverity: 'WARN',
    rationale: 'SCHADS weekly ordinary-hours ceiling before overtime provisions engage.',
  },
  MIN_ENGAGEMENT_BREACH: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'roster_coverage_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale: 'SCHADS minimum engagement period per shift.',
  },

  // ── Roster integrity — always applies, no mode ────────────────────────
  DOUBLE_BOOKING: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'BLOCK',
    neverSoften: true,
    rationale: 'One worker cannot be in two places. True in every configuration.',
  },
  WORKER_ON_LEAVE: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'BLOCK',
    // A worker on approved leave is a FACT, not a policy threshold. No enforcement posture makes them available, so this never relaxes — relaxedSeverity equals enforcedSeverity deliberately.
    relaxedSeverity: 'BLOCK',
    rationale: 'Worker has approved leave overlapping the shift.',
  },
  ABSENCE_PLANE_UNAVAILABLE: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'BLOCK',
    neverSoften: true,
    rationale:
      'Leave absence evidence could not be verified; roster gates and assignment must not proceed on a guessed absence answer.',
  },
  WORKER_NOT_AVAILABLE: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'roster_coverage_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale: 'Shift falls outside the worker declared availability.',
  },
  ASSIGNED_WORKER_TERMINATED: {
    publicationKind: 'CLINICAL_OR_LEGAL',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'BLOCK',
    relaxedSeverity: 'BLOCK',
    neverSoften: true,
    rationale: 'Terminated worker assigned to a future shift.',
  },

  // ── Cost and administrative ───────────────────────────────────────────
  COST_FORECAST_MISSING: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale: 'No cost forecast computed for the period.',
  },
  COST_BASELINE_MISSING: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale: 'No cost baseline to compare the forecast against.',
  },
  BUDGET_BREACH_ESTIMATE: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    governingMode: 'pay_context_enforcement_mode',
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'INFO',
    rationale: 'Forecast exceeds the period cost baseline.',
  },
  LATE_PUBLISH_NOTICE: {
    publicationKind: 'EXISTING',
    serviceModels: ['HOME_CARE', 'RESIDENTIAL'],
    enforcedSeverity: 'WARN',
    relaxedSeverity: 'WARN',
    rationale: 'Roster published inside the notice period the award requires.',
  },
} as const satisfies Record<string, GateApplicability>;

export const ROSTER_GATE_REGISTRY: Record<string, GateApplicability> = REGISTRY;

/** The only legal rule codes. Raise sites must use this type. */
export type RosterGateRuleCode = keyof typeof REGISTRY;

export const ROSTER_GATE_RULE_CODES = Object.keys(REGISTRY) as RosterGateRuleCode[];

export function isRosterGateRuleCode(code: string): code is RosterGateRuleCode {
  return Object.prototype.hasOwnProperty.call(REGISTRY, code);
}

export function gateApplicability(code: string): GateApplicability | null {
  return isRosterGateRuleCode(code) ? REGISTRY[code] : null;
}

/**
 * Rules whose declared unitOfWork disagrees with what detection measures today.
 * Keeping this explicit means the debt is assertable rather than forgotten;
 * the conformance test fails if an entry is removed without the detection fix.
 */
export const KNOWN_UNIT_DEFECTS: readonly RosterGateRuleCode[] = [
  // TEN_HOUR_BREAK unit fixed (same-day HC exempt; #1638). Daily hours still
  // measures a single shift rather than the full period-of-work span.
  'EXCESSIVE_DAILY_HOURS',
] as const;

const SEVERITY_ORDER: Record<GateSeverity, number> = { INFO: 0, WARN: 1, BLOCK: 2 };

export function severityAtMost(detected: GateSeverity, ceiling: GateSeverity): GateSeverity {
  return SEVERITY_ORDER[detected] <= SEVERITY_ORDER[ceiling] ? detected : ceiling;
}
